<template>
  <CustomRow>
    <v-card class="mt-5 text-center cusPadding">
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="10" sm="8" md="6">
          <div class="text-page-title font-weight-bold">
            {{ $t("register_title") }}
          </div>
          <v-form v-model="isFormValid" class="mt-15">
            <VuePhoneNumberInput
              class="mb-4"
              required
              size="lg"
              color="#8d8d8d"
              valid-color="#379cc4"
              :no-flags="true"
              error-color="#ff0032"
              :error="isMember || isPhoneNumberError"
              :translations="{
                countrySelectorLabel: $t('country_code'),
                phoneNumberLabel: $t('login_phone_field')
              }"
              :no-example="true"
              v-model="phoneNumberWithCode"
              @update="phoneNuberUupdate"
              @phone-number-blur="phoneNuberBlur"
            />
            <div class="v-text-field__details pl-3">
              <div class="v-messages theme--light error--text" role="alert">
                <div class="v-messages__wrapper">
                  <div
                    v-if="isShowPhoneNumberWarning"
                    class="v-messages__message"
                  >
                    {{ $t("input_required") }}
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="country !== 'TW'"
              class="v-alert p-4 text-left red--text pink-1 body-2 font-weight-regular rounded-lg"
              v-html="$t('mobile_hint')"
            ></div>
            <v-text-field
              class="mt-5"
              label="E-mail"
              outlined
              :hint="`${$t('change_email_example')}email@example.com`"
              color="primary-1"
              v-model="email"
              :rules="[inputRules.required, inputRules.email]"
              :error-messages="isMember ? $t('register_is_member') : ''"
            >
              <template #message="{ message }">
                {{ $t(message) }}
              </template>
            </v-text-field>

            <div class="grey lighten-5 pa-4">
              <div class="d-flex">
                <v-text-field
                  :label="$t('recaptcha_placeholder')"
                  color="primary-1"
                  v-model="captcha"
                  :rules="[inputRules.required]"
                  outlined
                >
                  <template #message="{ message }">
                    {{ $t(message) }}
                  </template></v-text-field
                >
                <div v-html="otp.data" class="captcha"></div>
              </div>
              <div
                class="primary-1--text align-content-start text-left"
                role="button"
                @click="renderOTP"
              >
                <v-icon color="primary-1">mdi-refresh</v-icon>
                {{ $t("recaptcha_refresh") }}
              </div>
            </div>

            <div class="d-flex">
              <v-spacer />
              <v-checkbox
                v-model="guarantee"
                class="mt-7 font-weight-bold"
                color="primary"
                dense
              >
                <template slot="label">
                  <span class="text-nine">
                    {{ $t("register_agree") }}
                    <span class="primary-1--text" @click="goTerms()">
                      &nbsp;{{ $t("register_terms") }}&nbsp;
                    </span>
                    {{ $t("register_and") }}
                    <span class="primary-1--text" @click="goTerms('privacy')">
                      &nbsp;{{ $t("register_privacy") }}</span
                    >
                  </span>
                </template>
              </v-checkbox>
              <v-spacer />
            </div>

            <v-btn
              class="font-weight-bold nextBtn"
              :class="{
                disabledBtn:
                  !isFormValid ||
                  !guarantee ||
                  !loadingFinished ||
                  isPhoneNumberError
              }"
              :disabled="
                !isFormValid ||
                  !guarantee ||
                  !loadingFinished ||
                  isPhoneNumberError
              "
              block
              depressed
              x-large
              @click="register"
            >
              <template v-if="!loadingFinished">
                <v-progress-circular
                  indeterminate
                  color="white"
                  size="25"
                  width="2"
                ></v-progress-circular>
              </template>
              <template v-else>
                {{ $t("register_cta") }}
              </template>
            </v-btn>
          </v-form>
        </v-col>

        <v-spacer></v-spacer>
      </v-row>
    </v-card>
  </CustomRow>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import UM from "@/api/user-management";
import TM from "@/api/ticket-management";
import inputRulesJS from "@/shared/inputRules.js";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";

export default {
  data() {
    return {
      otp: {
        key: null,
        data: ""
      },
      loadingFinished: true,
      isFormValid: false,
      phoneNumberWithCode: "",
      formattedNumber: "",
      country: "",
      countryCode: "",
      mobile: "",
      email: "",
      captcha: "",
      showPassword: false,
      showAgainPassword: false,
      inputRules: {
        required: inputRulesJS.required,
        email: inputRulesJS.email
      },
      guarantee: false,
      isMember: false,
      isPhoneNumberError: false,
      isShowPhoneNumberWarning: false
    };
  },
  components: {
    VuePhoneNumberInput
  },
  metaInfo() {
    return {
      title: `${this.$t("register_title")} - ${this.$t("app_name")}`,
      script: [
        {
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: `${this.$t("register_title")} - ${this.$t("app_name")}`,
            url: window.location.href
          }
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      dialog: "dialog/dialog"
    })
  },
  methods: {
    ...mapActions({
      showDialog: "dialog/show",
      storeRegisterInfo: "register/setRegister"
    }),
    goTerms(privacy) {
      if (!privacy) {
        this.$router.push({ name: "Terms" }).catch(() => {});
      } else {
        this.$router.push({ name: "Terms", hash: "#privacy" }).catch(() => {});
      }
    },
    async register() {
      this.loadingFinished = false;

      // 驗證手機
      const { data } = await UM.verify(
        this.countryCode,
        this.mobile.replaceAll(/\D/g, "")
      );
      // console.log(`${String.fromCodePoint(128049)} [UM] verify mobile`, data);
      if (data.errCode === "00") {
        if (data.isMember) {
          this.isMember = data.isMember;
        } else {
          // 發送OTP
          const { data: otpRes } = await UM.otp(
            this.countryCode,
            this.mobile.replaceAll(/\D/g, ""),
            {
              key: this.otp.key,
              ans: this.captcha
            }
          );
          // console.log(`${String.fromCodePoint(128049)} [UM] otp`, otpRes);
          if (otpRes.errCode === "00") {
            this.storeRegisterInfo({
              formattedNumber: this.formattedNumber,
              countryCode: this.countryCode,
              mobile: this.mobile.replaceAll(/\D/g, ""),
              email: this.email
            });
            this.$router.push({ name: "registerSteps" }).catch(() => {});
          } else console.error("發送OTP失敗");
          // todo handle otp fail
        }
      } else {
        // todo handle verify fail
      }

      this.loadingFinished = true;
    },
    phoneNuberBlur() {
      if (!this.phoneNumberWithCode) {
        this.isPhoneNumberError = true;
        this.isShowPhoneNumberWarning = true;
      }
    },
    phoneNuberUupdate(phoneNumberObject) {
      this.isMember = false;
      this.mobile = phoneNumberObject?.phoneNumber || "";
      this.formattedNumber = phoneNumberObject?.formattedNumber || "";
      this.countryCode = phoneNumberObject?.countryCallingCode || "";
      this.country = phoneNumberObject?.countryCode || "";

      let isValidHK = true;
      // UI 定義的特殊行為，當初始顯示時不要檢查是否錯誤，維持元件的框為黑色，
      // 但是元件 init 後顯示時會自動呼叫 updatd event，導致初始顯示時就會當成驗證失敗的狀態，
      // bypass 沒輸入電話時的檢查行為
      if (phoneNumberObject?.countryCode === "HK") {
        let num = phoneNumberObject?.phoneNumber.replace(" ", "");
        isValidHK = /^([4|5|6|7|8|9])\d{7}$/.test(num);
      }
      if (
        this.phoneNumberWithCode === "" &&
        phoneNumberObject?.phoneNumber === undefined
      ) {
        return;
      }

      if (
        !this.mobile ||
        (phoneNumberObject?.isValid === false && !isValidHK)
      ) {
        this.isPhoneNumberError = true;
      } else {
        this.isPhoneNumberError = false;
      }
      if (phoneNumberObject?.phoneNumber === undefined) {
        this.isShowPhoneNumberWarning = true;
      } else {
        this.isShowPhoneNumberWarning = false;
      }
    },
    getCountryCode() {
      const countrySelect = document.querySelector(".country-selector__input");

      if (!countrySelect) {
        setTimeout(() => {
          this.getCountryCode();
        }, 300);

        return;
      }
      this.countryCode = countrySelect.value;
      countrySelect.addEventListener("change", e => {});
    },
    async renderOTP() {
      const { data } = await TM.generateOTP();
      const svg = data.data.replace("213", "170");
      this.otp = {
        key: data.key,
        data: svg.replace("75", "55")
      };
    }
  },
  mounted() {
    this.getCountryCode();
    this.renderOTP();
  }
};
</script>

<style lang="scss" scoped>
.cusPadding {
  padding: 50px 20px;
}

.captcha {
  width: 170px;
}
</style>
